import { useCallback } from 'react';

import { ILocation } from 'hooks/use-map/types';
import { isValidPosition } from 'utils/geolocation';

import { StoreCardListRef } from '../components';
import { FocusedBy, MapViewRef } from '../components/map-view/types';
import {
  OnRestaurantHighlight,
  StoreLocatorActionType,
  StoreLocatorActions,
} from '../components/types';
import { StoreLocatorAction } from '../components/types';
import { IStore } from '../ducks/stores';

/**
 * This hook is used to handle the actions of the store locator.
 * search by area, search by address, and reset the user's or manual coordinates.
 */
export const useStoreLocatorHandlers = ({
  actions,
  setSearchThisAreaCoordinates,
  fetchAndSortNearbyRestaurantsAsync,
  highlightStore,
  setManualAddress,
  updateManualCoordinates,
  isPermissionGranted,
  resetCoordinatesManual,
  mapRef,
  cachedStoresNearby,
  userCoordinates,
  loadCurrentPositionAsync,
  flatListRef,
}: {
  actions: React.MutableRefObject<StoreLocatorActions>;
  setSearchThisAreaCoordinates: (coordinates: ILocation | null) => void;
  fetchAndSortNearbyRestaurantsAsync: (coordinates: ILocation) => Promise<IStore[]>;
  highlightStore: OnRestaurantHighlight;
  setManualAddress: (address: string) => void;
  updateManualCoordinates: (address: string, placeId?: string) => Promise<ILocation | null>;
  isPermissionGranted: boolean;
  resetCoordinatesManual: () => void;
  mapRef: React.RefObject<MapViewRef>;
  cachedStoresNearby: IStore[];
  userCoordinates: ILocation | null;
  loadCurrentPositionAsync: () => Promise<ILocation | undefined>;
  flatListRef: React.RefObject<StoreCardListRef>;
}) => {
  const handleSearchThisArea = useCallback(
    async (location: ILocation) => {
      actions.current[StoreLocatorActionType.AREA_SEARCH] = true;
      setSearchThisAreaCoordinates(location);
      const stores = await fetchAndSortNearbyRestaurantsAsync(location);
      if (stores?.[0]) {
        highlightStore({
          restaurant: stores?.[0],
          coords: location,
        });
      }
    },
    [actions, setSearchThisAreaCoordinates, fetchAndSortNearbyRestaurantsAsync, highlightStore]
  );

  const handleAddressSelected = useCallback(
    async (address: string, placeId?: string) => {
      actions.current[StoreLocatorActionType.MANUAL_SEARCH] = true;
      setManualAddress(address);
      const manualCoordinates = await updateManualCoordinates(address, placeId);
      if (manualCoordinates?.lat && manualCoordinates?.lng) {
        const stores = await fetchAndSortNearbyRestaurantsAsync(manualCoordinates);
        if (stores?.[0]) {
          highlightStore({
            restaurant: stores?.[0],
            coords: manualCoordinates,
          });
        }
      }
    },
    [
      actions,
      setManualAddress,
      updateManualCoordinates,
      fetchAndSortNearbyRestaurantsAsync,
      highlightStore,
    ]
  );

  const handleResetUserCoords = useCallback(async () => {
    if (!isPermissionGranted) {
      return;
    }

    resetCoordinatesManual();
    setSearchThisAreaCoordinates(null);

    mapRef.current?.focus({
      restaurant: cachedStoresNearby?.[0],
      focusReason: StoreLocatorAction.isReset,
      coordinates: userCoordinates ?? undefined,
    });

    const currentUserCoordinates = await loadCurrentPositionAsync();

    if (isValidPosition(currentUserCoordinates)) {
      actions.current[StoreLocatorActionType.LOCATION_RESET] = true;
      const stores = await fetchAndSortNearbyRestaurantsAsync(currentUserCoordinates);
      if (stores?.[0]) {
        flatListRef.current?.scrollToTop();
        highlightStore({
          restaurant: stores?.[0],
          focusedBy: FocusedBy.GLOBAL,
          coords: currentUserCoordinates,
        });
      }
    }
  }, [
    isPermissionGranted,
    resetCoordinatesManual,
    setSearchThisAreaCoordinates,
    mapRef,
    cachedStoresNearby,
    userCoordinates,
    loadCurrentPositionAsync,
    actions,
    fetchAndSortNearbyRestaurantsAsync,
    flatListRef,
    highlightStore,
  ]);

  const handleResetManualSearch = useCallback(async () => {
    resetCoordinatesManual();
    handleResetUserCoords();
  }, [resetCoordinatesManual, handleResetUserCoords]);

  return {
    handleSearchThisArea,
    handleAddressSelected,
    handleResetUserCoords,
    handleResetManualSearch,
  };
};
